import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Snackbar } from "@material-ui/core";
import MuiAlert, { AlertProps } from "@material-ui/lab/Alert";
import { useTranslation } from "react-i18next";
import BankCards from "../../components/BankCards/BankCards";
import { useBankCards } from "./useBankCards";

const Alert: React.FC<AlertProps> = (props) => (
  <MuiAlert elevation={6} variant="filled" {...props} />
);

const BankCardsPage: React.FC = () => {
  const { t } = useTranslation();
  const { items, isLoading, deleteCard, addCard, isAddingCard } =
    useBankCards();
  const history = useHistory();
  const location = useLocation();

  const [removingId, setRemovingId] = React.useState<string | null>(null);
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const handleSnackbarClose = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  React.useEffect(() => {
    window.scrollTo(0, 0);

    const params = new URLSearchParams(location.search);
    const paymentStatus = params.get("paymentStatus");

    if (paymentStatus === "success") {
      setSnackbar({
        open: true,
        message: t("account.paymentCardAdded"),
        severity: "success",
      });
    } else if (paymentStatus === "error") {
      setSnackbar({
        open: true,
        message: t("account.paymentCardAddError"),
        severity: "error",
      });
    }

    if (paymentStatus) {
      history.replace(location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRemove = async (id: string) => {
    setRemovingId(id);
    const result = await deleteCard(id);
    setRemovingId(null);

    setSnackbar({
      open: true,
      message: result.ok
        ? t("account.paymentCardRemoved")
        : t("account.paymentCardRemoveError"),
      severity: result.ok ? "success" : "error",
    });
  };

  const onAddCard = async () => {
    const result = await addCard();

    if (!result.ok) {
      setSnackbar({
        open: true,
        message: t("account.paymentCardAddError"),
        severity: "error",
      });
    }
  };

  return (
    <>
      <BankCards
        items={items}
        isLoading={isLoading}
        removingId={removingId}
        onRemove={onRemove}
        onAddCard={onAddCard}
        isAddingCard={isAddingCard}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: t("css.snackBarPosition") as "left" | "right" | "center",
        }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BankCardsPage;
